import { watch, toRaw } from 'vue';

const defaultOptions = {
	storage: 'local',
	pick: undefined,
	serializer: {
		serialize: JSON.stringify,
		deserialize: JSON.parse,
	},
	namespace: undefined, // Per-config namespace override
};

// Writes at least this big are logged with their size, to spot payloads that
// should not be crossing the native bridge
const LARGE_WRITE_LOG_THRESHOLD = 50_000;

// Storage adapters registry (injected via plugin config)
let registeredStorages = {};

// Global namespace for all stores (injected via plugin config)
let globalNamespace;

/**
 * Get storage adapter by name
 * @param {string} storageName - The storage adapter name
 * @returns {Object} The storage adapter
 * @throws {Error} If storage adapter not found
 */
function getStorageAdapter (storageName) {
	const adapter = registeredStorages[storageName];

	if (!adapter) {
		throw new Error(
			`Storage adapter "${storageName}" not found. ` +
            `Available adapters: ${Object.keys(registeredStorages).join(', ')}`,
		);
	}

	return adapter;
}

/**
 * Generate storage key with namespace support
 * @param {string} key - The state property key
 * @param {Object} options - Options
 * @param {string} options.storeName - Store name (e.g., 'auth', 'faces')
 * @param {string|false|undefined} options.namespace - Namespace override (false = disable, undefined = use global)
 * @returns {string} The final storage key
 *
 * @example
 * // With global namespace 'sesame-wall-v3'
 * generateKey('token', { storeName: 'auth' })
 * // Returns: 'sesame-wall-v3:auth:token'
 *
 * @example
 * // With namespace override
 * generateKey('token', { storeName: 'auth', namespace: 'custom' })
 * // Returns: 'custom:auth:token'
 *
 * @example
 * // Namespace disabled
 * generateKey('token', { storeName: 'auth', namespace: false })
 * // Returns: 'token' (original behavior)
 */
function generateKey (key, options = {}) {
	const { storeName, namespace } = options;

	// namespace: false → disable namespace entirely (backwards compatible)
	if (namespace === false) {
		return key;
	}

	// Determine which namespace to use
	const activeNamespace = namespace !== undefined ? namespace : globalNamespace;

	// No namespace → return original key (backwards compatible)
	if (!activeNamespace) {
		return key;
	}

	// Build namespaced key: {namespace}:{storeName}:{key}
	return `${activeNamespace}:${storeName}:${key}`;
}

/**
 * Migrate store properties from one storage to another with key mapping support
 * @param {Object} state - Store state
 * @param {Array} keys - Keys to migrate (state property names)
 * @param {Object} options - Migration options
 * @param {string} options.from - Source storage adapter name
 * @param {string} options.to - Target storage adapter name
 * @param {Object} options.serializer - Serializer
 * @param {Object} options.keyMap - Manual key mapping (oldKey → newKey)
 * @param {boolean} options.addNamespace - Auto-generate namespaced keys
 * @param {string} options.storeName - Store name (for namespace generation)
 * @param {string|false} options.namespace - Namespace for key generation
 */
function migrateStoreProperties (state, keys = Object.keys(state), options) {
	const { from, to, serializer, keyMap, addNamespace, storeName, namespace } = options;
	const sourceAdapter = getStorageAdapter(from);
	const targetAdapter = getStorageAdapter(to);

	// console.log(`🔄 [persistence] Starting migration from "${from}" to "${to}" for ${keys.length} keys...`);

	return Promise.all(
		keys.map(async (key) => {
			try {
				// Determine source key (old key)
				let sourceKey = key;

				// Determine target key (new key)
				let targetKey = key;

				if (keyMap && keyMap[key]) {
					// Manual key mapping provided
					sourceKey = keyMap[key].from || key;
					targetKey = keyMap[key].to || key;
				} else if (addNamespace) {
					// Auto-generate namespaced key for target
					// Source key is the original (no namespace)
					sourceKey = key;
					targetKey = generateKey(key, { storeName, namespace });
				}

				// Try to get value from source storage using source key
				let { value } = await sourceAdapter.get({ key: sourceKey });
				value = serializer.deserialize(value);

				if (value !== undefined && value !== null) {
					// Save to target storage using target key
					await targetAdapter.set({ key: targetKey, value: serializer.serialize(value) });
					// Remove from source storage using source key
					await sourceAdapter.remove({ key: sourceKey });

					if (sourceKey !== targetKey) {
						// console.log(`✅ [persistence] Migrated "${sourceKey}" → "${targetKey}"`);
					} else {
						// console.log(`✅ [persistence] Migrated "${key}" from "${from}" to "${to}"`);
					}
				}
			} catch (error) {
				// Silently skip keys that don't exist in source storage (expected behavior)
				const isNotFound = error.message?.toLowerCase().includes('not found');
				if (!isNotFound) {
					// Only log real errors, not "key not found"
					// console.error(`❌ [persistence] Migration failed for "${key}":`, error);
				}
				// Continue with other keys
			}
		}),
	);
};

/**
 * Wrap store properties with persistence watchers
 * @param {Object} state - Store state
 * @param {Array} keys - Keys to persist
 * @param {Object} options - Options
 * @param {string} options.storage - Storage adapter name
 * @param {Object} options.serializer - Serializer
 * @param {string} options.storeName - Store name (for namespace generation)
 * @param {string|false} options.namespace - Namespace for key generation
 */
function wrapStoreProperties (state, keys = Object.keys(state), options) {
	const { storage, serializer, storeName, namespace } = options;
	const adapter = getStorageAdapter(storage);

	return Promise.all(
		keys.map(async (key) => {
			const defaultValue = state[key];

			// Generate storage key (with namespace support)
			const storageKey = generateKey(key, { storeName, namespace });

			// console.log(`📦 [persistence] Loading "${key}" from "${storage}" (storageKey: "${storageKey}")`);

			try {
				const { value } = await adapter.get({ key: storageKey });
				// console.log(`📦 [persistence] Raw value for "${key}":`, value);
				const deserializedValue = serializer.deserialize(value);
				// console.log(`📦 [persistence] Deserialized value for "${key}":`, deserializedValue);
				state[key] = deserializedValue ?? defaultValue;
				// console.log(`✅ [persistence] Successfully loaded "${key}" into state`);
			} catch (error) {
				// Silently use default value if key not found (expected on first load)
				const isNotFound = error.message?.toLowerCase().includes('not found');
				if (!isNotFound) {
					// Only log real errors, not "key not found"
					// console.error(`❌💥 [persistence] Failed to load ${key}, using default:`, error);
				} else {
					// console.log(`ℹ️ [persistence] Key "${key}" not found in storage, using default value`);
				}
				state[key] = defaultValue;
			}
			// One write in flight and at most one pending per key. The value is read
			// and serialized when the write starts, not when the change happens, so
			// a burst of changes collapses into a single write that carries the
			// latest state, and only one serialized snapshot is alive at a time.
			let inFlight = false;
			let dirty = false;

			async function flush () {
				if (inFlight) {
					dirty = true;
					return;
				}

				inFlight = true;

				try {
					const value = toRaw(state[key]);

					if (value === undefined || value === null) {
						try {
							await adapter.remove({ key: storageKey });
						} catch (error) {
							console.error(`[persistence] Remove failed for ${storageKey}:`, error);
						}
						return;
					}

					let serialized;

					try {
						serialized = serializer.serialize(value);
					} catch (error) {
						console.error(`[persistence] Serialize failed for ${storageKey}:`, error);
						return;
					}

					try {
						await adapter.set({
							key: storageKey,
							value: serialized,
						});

						// warn, not log: it must survive production builds to be seen in logcat
						if (serialized.length >= LARGE_WRITE_LOG_THRESHOLD) {
							console.warn(`[persistence] set ${storageKey} (${serialized.length} chars)`);
						}
					} catch (error) {
						console.error(`[persistence] Save failed for ${storageKey}:`, error);
					}
				} finally {
					inFlight = false;

					if (dirty) {
						dirty = false;
						flush();
					}
				}
			}

			watch(() => state[key], () => {
				flush();
			}, { deep: true });
		}),
	);
}

/**
 * Validate that no state property is persisted in multiple configs
 * @param {Array} persistConfigs - Array of persist configurations
 * @param {Object} state - Store state
 * @throws {Error} If duplicate keys found
 */
function validateNoDuplicateKeys (persistConfigs, state) {
	const keyToStorage = new Map();

	for (const config of persistConfigs) {
		const pick = config.pick || Object.keys(state);
		const storage = config.storage || defaultOptions.storage;

		for (const key of pick) {
			if (keyToStorage.has(key)) {
				throw new Error(
					`[persist-plugin] Duplicate key "${key}" found in multiple persist configs. ` +
                    `Already persisted to "${keyToStorage.get(key)}", cannot also persist to "${storage}". ` +
                    'Each state property can only be persisted once.',
				);
			}
			keyToStorage.set(key, storage);
		}
	}
}

/**
 * Create persist plugin with storage adapters configuration
 *
 * @param {Object} config - Plugin configuration
 * @param {Object} config.storages - Storage adapters registry
 * @returns {Function} The persist plugin function
 *
 * @example
 * // Register storage adapters
 * const persist = usePersistStorePlugin({
 *   storages: {
 *     local: createLocalStorageAdapter(),
 *     filesystem: createFilesystemAdapter(),
 *     native: createNativePreferencesAdapter(),
 *   }
 * });
 *
 * @example
 * // Single persist config (backwards compatible)
 * persist: {
 *   storage: 'native',
 *   pick: ['token', 'user'],
 * }
 *
 * @example
 * // Multiple persist configs
 * persist: [
 *   { storage: 'filesystem', pick: ['faces'] },
 *   { storage: 'native', pick: ['audio', 'threshold'] },
 * ]
 *
 * @example
 * // Migration (opt-in)
 * persist: {
 *   storage: 'native',
 *   pick: ['token'],
 *   migrate: 'local',  // Migrate from localStorage
 * }
 *
 * @example
 * // Multiple configs with migration
 * persist: [
 *   { storage: 'filesystem', pick: ['faces'], migrate: 'local' },
 *   { storage: 'native', pick: ['settings'], migrate: { from: 'local' } },
 * ]
 */
export function usePersistStorePlugin (config = {}) {
	// Register storage adapters
	if (config.storages) {
		registeredStorages = { ...config.storages };
		// console.log(`✅ [persist-plugin] Registered storage adapters: ${Object.keys(registeredStorages).join(', ')}`);
	}

	// Set global namespace (opt-in)
	if (config.namespace) {
		globalNamespace = config.namespace;
		// console.log(`📦 [persist-plugin] Global namespace: "${globalNamespace}"`);
	}

	// Return the actual plugin function
	return async function persistPlugin ({ state, options }) {
		if (!options.persist) return;

		// Get store name from options
		const storeName = options.name || 'store';
		// console.log(`🔌 [persist-plugin] Starting persistence for store "${storeName}"`);

		// Define isReady function SYNCHRONOUSLY before any awaits
		// This ensures it's available immediately when the store is accessed
		options.isReady = function () {
			return new Promise(resolve => {
				function checkReady () {
					if (state.ready) {
						// console.log(`✅ [persist-plugin] Store "${storeName}" isReady check: ready = true`);
						resolve(true);
					} else {
						// console.log(`⏳ [persist-plugin] Store "${storeName}" isReady check: not ready yet, polling...`);
						setTimeout(checkReady, 100);
					}
				}
				checkReady();
			});
		};

		// Normalize persist to array (backwards compatible)
		const persistConfigs = Array.isArray(options.persist)
			? options.persist
			: [options.persist];

		// Validate: no duplicate keys across configs
		validateNoDuplicateKeys(persistConfigs, state);

		// Apply each persist config independently
		for (const persistConfig of persistConfigs) {
			const { storage, pick, serializer, migrate, namespace } = {
				...defaultOptions,
				...persistConfig,
			};

			// Optional migration (opt-in)
			if (migrate) {
				// Support both string and object format
				// migrate: 'local' → migrate from 'local' to current storage
				// migrate: { from: 'local', to: 'native', keyMap, addNamespace } → explicit options
				const migrateFrom = typeof migrate === 'string' ? migrate : migrate.from;
				const migrateTo = typeof migrate === 'string' ? storage : (migrate.to || storage);
				const keyMap = typeof migrate === 'object' ? migrate.keyMap : undefined;
				const addNamespace = typeof migrate === 'object' ? migrate.addNamespace : false;

				if (migrateFrom && migrateTo && migrateFrom !== migrateTo) {
					await migrateStoreProperties(state, pick, {
						from: migrateFrom,
						to: migrateTo,
						serializer,
						keyMap,
						addNamespace,
						storeName,
						namespace,
					});
				}
			}

			// Watch properties for this specific storage
			await wrapStoreProperties(state, pick, {
				storage,
				serializer,
				storeName,
				namespace,
			});
		}

		// All async operations complete, mark store as ready
		// console.log(`✅ [persist-plugin] Store "${storeName}" persistence complete, setting ready = true`);
		state.ready = true;
	};
}
